/**
 * Aspect — LightSystem (ceiling lights + wall switches placed in the 2D planner)
 * --------------------------------------------------------------
 * The plan editor lets the user drop ceiling-light fixtures (ceiling/pendant/
 * spot/chandelier) inside a room and light switches anywhere. The server carries
 * both on the room (meters, room-origin) tagged with a `group` (one per room).
 *
 * Here we build a visible fixture + a real PlayCanvas light for each light, and a
 * clickable switch plate for each switch. Clicking a switch (or pressing F while
 * looking at it) toggles every light that shares its `group` on/off. Switches sit
 * on wall centerlines and face into the room via `normal` (world XZ). Fixtures are
 * parented to `scene.roomRoot`, so they tear down with the room on every reload.
 */

import * as pc from "playcanvas";
import { hourToSunElevation } from "../scene/day_night_cycle.js";

const INTERACT_RANGE = 3.4; // how close the player must be to flip a switch

/** 0 in full daylight → 1 at night (mirrors DayNightCycle's day curve). */
function darknessAt(hour) {
  const elev = hourToSunElevation(hour);
  const t = Math.max(0, Math.min(1, (elev + 8) / 28));
  return 1 - t * t * (3 - 2 * t);
}

// Per-fixture look + light preset. `color` is the emitted light colour; `range`
// and `intensity` size the pool; `drop` hangs the lamp below the ceiling.
// Ranges stay near room size: these lights cast no shadows, so anything wider
// bleeds straight through walls into the neighbouring rooms.
// Realistic warm-white bulbs (~2800–3200K) — soft warmth, never amber-orange.
const FIXTURES = {
  ceiling: { color: [1.0, 0.97, 0.93], intensity: 0.85, range: 4.5, drop: 0.04, kind: "point" },
  pendant: { color: [1.0, 0.95, 0.88], intensity: 1.0, range: 4.0, drop: 0.55, kind: "point" },
  spot: { color: [1.0, 0.97, 0.92], intensity: 1.4, range: 4.5, drop: 0.05, kind: "spot" },
  chandelier: { color: [1.0, 0.96, 0.9], intensity: 1.15, range: 4.5, drop: 0.7, kind: "point" },
};
const fixtureOf = (k) => FIXTURES[String(k || "").toLowerCase()] ?? FIXTURES.ceiling;

function screenRay(camera, x, y) {
  const cam = camera.camera;
  const near = cam.screenToWorld(x, y, cam.nearClip);
  const far = cam.screenToWorld(x, y, cam.farClip);
  const dir = far.clone().sub(near).normalize();
  return { origin: near, dir };
}

function worldAABB(entity) {
  const aabb = new pc.BoundingBox();
  let ok = false;
  for (const render of entity.findComponents("render")) {
    for (const mi of render.meshInstances) {
      if (!ok) {
        aabb.copy(mi.aabb);
        ok = true;
      } else {
        aabb.add(mi.aabb);
      }
    }
  }
  return ok ? aabb : null;
}

function rayAABB(origin, dir, aabb) {
  const min = aabb.getMin();
  const max = aabb.getMax();
  let tmin = -Infinity;
  let tmax = Infinity;
  for (const axis of ["x", "y", "z"]) {
    const o = origin[axis];
    const d = dir[axis];
    if (Math.abs(d) < 1e-9) {
      if (o < min[axis] || o > max[axis]) return null;
    } else {
      let t1 = (min[axis] - o) / d;
      let t2 = (max[axis] - o) / d;
      if (t1 > t2) [t1, t2] = [t2, t1];
      tmin = Math.max(tmin, t1);
      tmax = Math.min(tmax, t2);
      if (tmin > tmax) return null;
    }
  }
  return tmax >= 0 ? (tmin >= 0 ? tmin : 0) : null;
}

export class LightSystem {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   */
  constructor(app, camera, scene) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;

    /** @type {Map<string, object>} lightId -> { entity, light, group, fixture, bulbMat } */
    this.lights = new Map();
    /** @type {Map<string, object>} switchId -> { entity, group, plateMat, on } */
    this.switches = new Map();
    /** group -> on/off (defaults on). */
    this.groupOn = new Map();
    this._targetSwitchId = null;
    /** 0 (day) → 1 (night); lit fixtures glow warmer/brighter after dark. */
    this._darkness = 0;

    this.app.on("time:changed", ({ hour }) => {
      const d = darknessAt(hour);
      if (Math.abs(d - this._darkness) < 0.02) return;
      this._darkness = d;
      for (const [group, on] of this.groupOn) {
        if (on) this._applyGroup(group, true);
      }
      this._scaleModelEmissives();
    });
    // Lamp models stream in lazily — keep their glow matched to the clock.
    this._emissiveAcc = 0;
    this.app.on("update", (dt) => {
      this._emissiveAcc += dt ?? 0.016;
      if (this._emissiveAcc < 2) return;
      this._emissiveAcc = 0;
      this._scaleModelEmissives();
    });

    this.app.on("scene:room-loaded", (room) => this._onRoom(room));
    this.app.on("scene:lights-patched", () => {
      if (this.scene?.room) this._onRoom(this.scene.room);
    });
    this.app.on("look:target-changed", ({ kind, id }) => {
      this._targetSwitchId = kind === "switch" ? id : null;
    });
    this.app.keyboard?.on(pc.EVENT_KEYDOWN, (e) => {
      if (e.key === pc.KEY_F && this._targetSwitchId) this.toggleSwitch(this._targetSwitchId);
    });
  }

  _onRoom(room) {
    for (const { entity } of this.lights.values()) entity?.destroy();
    for (const { entity } of this.switches.values()) entity?.destroy();
    this.lights.clear();
    this.switches.clear();
    this._room = room;
    this._roomRects = Array.isArray(room?.rooms) ? room.rooms : [];
    if (!this.scene.roomRoot || !room) return;

    const ceilH = room.dimensions?.height ?? 2.8;
    const floorY = room.floorY ?? 0;
    const ceilingY = floorY + ceilH;

    let n = 0;
    for (const l of room.lights ?? []) {
      this._spawnLight(l.id ?? `L${++n}`, l, ceilingY);
    }
    this._addCorridorSatellites(ceilingY);
    let s = 0;
    for (const sw of room.switches ?? []) {
      this._spawnSwitch(sw.id ?? `S${++s}`, sw, floorY);
    }
    // Apply every group once so multi-fixture rooms get their crowd-normalized
    // intensity (and remembered on/off states re-assert across rebuilds).
    for (const [group, on] of this.groupOn) this._applyGroup(group, on);
  }

  /** How many lights share a group — used to normalize stacked intensity. */
  _groupSize(group) {
    let n = 0;
    for (const l of this.lights.values()) if (l.group === group) n++;
    return Math.max(1, n);
  }

  /** Room rect for a light group (group == room index in the plan). */
  _groupRect(group) {
    const r = this._roomRects?.[group];
    if (!r) return null;
    const w = Number(r.width) || 0;
    const d = Number(r.depth) || 0;
    return w > 0.2 && d > 0.2 ? { width: w, depth: d, area: w * d } : null;
  }

  /**
   * Per-light brightness factor, normalized to the BEDROOM's night look:
   * one warm ~1.2-intensity pool per ~46 m². Every room gets the same light
   * DENSITY (total budget ∝ area), split across its fixtures — so a 6-bulb
   * kitchen glows in soft pools like the bedroom instead of a flat wash, and
   * single-lamp rooms match the bedroom exactly.
   */
  _groupFactor(group) {
    const n = this._groupSize(group);
    const area = this._groupRect(group)?.area ?? 24;
    return Math.min(1.3, Math.max(0.32, ((area / 46) * 1.15) / n));
  }

  /**
   * Very long rooms (the hallway) can never match the bedroom's night look
   * with one bulb — its single pool leaves the corridor ends black. Spawn
   * satellite light sources on the same group (same wall switch, shared
   * area budget) spaced along the long axis, so the whole corridor glows in
   * evenly-spaced warm pools like the bedroom.
   */
  _addCorridorSatellites(ceilingY) {
    const rects = this._roomRects;
    const min = this._room?.bounds?.min ?? [0, 0, 0];
    if (!Array.isArray(rects)) return;
    for (let group = 0; group < rects.length; group++) {
      const r = rects[group];
      const w = Number(r?.width) || 0;
      const d = Number(r?.depth) || 0;
      const span = Math.max(w, d);
      if (span < 10) continue;
      const existing = [...this.lights.values()].filter((l) => l.group === group);
      if (!existing.length) continue;
      const want = Math.ceil(span / 8);
      if (existing.length >= want) continue;

      const x0 = min[0] + (Number(r.x) || 0);
      const z0 = min[2] + (Number(r.z) || 0);
      const alongX = w >= d;
      const template = existing[0];
      let added = 0;
      for (let i = 0; i < want; i++) {
        const t = (i + 0.5) / want;
        const px = alongX ? x0 + t * w : x0 + w / 2;
        const pz = alongX ? z0 + d / 2 : z0 + t * d;
        // Skip slots already covered by a real fixture's source.
        const near = existing.some((l) => {
          const p = l.light?.getLocalPosition?.();
          return p && Math.hypot(p.x - px, p.z - pz) < 3;
        });
        if (near) continue;
        this._spawnLight(
          `${group}-sat-${++added}`,
          {
            position: [px, pz],
            group,
            fixture: template.fixture ?? "ceiling",
            on: this.groupOn.get(group) !== false,
          },
          ceilingY,
        );
      }
    }
  }

  /**
   * Lamp MODELS (HouseProps shades, clock faces) carry baked emissive maps.
   * Scale their glow with darkness so fixtures read OFF in daylight — the sun
   * carries the scene — and warm up through dusk into night.
   */
  _scaleModelEmissives() {
    const mats = this.scene?.registry?.emissiveLampMats;
    if (!mats?.size) return;
    const f = 0.06 + 0.94 * Math.pow(this._darkness, 1.2);
    for (const [mat, base] of mats) {
      const next = base * f;
      if (Math.abs((mat.emissiveIntensity ?? 0) - next) < 0.02) continue;
      mat.emissiveIntensity = next;
      mat.update();
    }
  }

  _spawnLight(id, data, ceilingY) {
    const pos = Array.isArray(data.position) ? data.position : [0, 0];
    const group = Number.isFinite(Number(data.group)) ? Number(data.group) : -1;
    const fx = fixtureOf(data.fixture);
    const col = new pc.Color(fx.color[0], fx.color[1], fx.color[2]);
    const on = this.groupOn.has(group) ? this.groupOn.get(group) : data.on !== false;
    this.groupOn.set(group, on);

    const root = new pc.Entity(`light-${id}`);
    this.scene.roomRoot.addChild(root);
    const lampY = ceilingY - fx.drop;

    // No procedural fixture meshes (canopy/cord/bulb): the visible fixtures are
    // real catalog models (HouseProps chandeliers, ceiling lamps, fans) placed
    // as furniture at the same spots — this entity is ONLY the light source.
    const canopy = null;
    const bulb = null;
    const bulbMat = null;

    // The actual light source. Range grows with the room so pools reach the
    // walls of big rooms (kitchen/living) instead of fading mid-floor.
    const rect = this._groupRect(group);
    const roomSpan = rect ? Math.max(rect.width, rect.depth) : 0;
    const range = Math.min(9.5, Math.max(fx.range, roomSpan * 0.62));
    const lightEnt = new pc.Entity(`light-src-${id}`);
    if (fx.kind === "spot") {
      lightEnt.addComponent("light", {
        type: "spot",
        color: col,
        intensity: 0, // _applyGroup sets the live level (time/area aware)
        range,
        innerConeAngle: 28,
        outerConeAngle: 50,
        castShadows: false,
      });
      lightEnt.setLocalPosition(pos[0], lampY, pos[1]);
      lightEnt.setLocalEulerAngles(90, 0, 0); // point straight down
    } else {
      lightEnt.addComponent("light", {
        type: "omni",
        color: col,
        intensity: 0, // _applyGroup sets the live level (time/area aware)
        range,
        castShadows: false,
      });
      lightEnt.setLocalPosition(pos[0], lampY, pos[1]);
    }
    root.addChild(lightEnt);

    this.lights.set(String(id), {
      entity: root,
      bulb,
      canopy,
      light: lightEnt,
      bulbMat,
      group,
      fixture: data.fixture,
      intensity: fx.intensity,
    });
  }

  /** @returns {{ id:string, kind:'light', entity:pc.Entity, fixture:string }[]} */
  interactables() {
    const out = [];
    for (const [id, rec] of this.lights) {
      if (rec.bulb) out.push({ id, kind: "light", entity: rec.bulb, fixture: rec.fixture });
      if (rec.canopy) out.push({ id, kind: "light", entity: rec.canopy, fixture: rec.fixture });
    }
    return out;
  }

  _spawnSwitch(id, data, floorY) {
    const pos = Array.isArray(data.position) ? data.position : [0, 0];
    const group = Number.isFinite(Number(data.group)) ? Number(data.group) : -1;
    const on = this.groupOn.has(group) ? this.groupOn.get(group) : data.on !== false;
    this.groupOn.set(group, on);

    const normal = Array.isArray(data.normal) ? data.normal : [0, 1];
    let nx = Number(normal[0]) || 0;
    let nz = Number(normal[1]);
    if (!Number.isFinite(nz)) nz = 1;
    const nlen = Math.hypot(nx, nz) || 1;
    nx /= nlen;
    nz /= nlen;
    const yaw = Math.atan2(nx, nz) * pc.math.RAD_TO_DEG;
    const plateDepth = 0.03;

    const root = new pc.Entity(`switch-${id}`);
    root.setLocalPosition(pos[0], floorY, pos[1]);
    root.setLocalEulerAngles(0, yaw, 0);
    this.scene.roomRoot.addChild(root);

    const plateMat = new pc.StandardMaterial();
    plateMat.diffuse = new pc.Color(0.92, 0.92, 0.95);
    plateMat.gloss = 0.3;
    plateMat.update();
    const plate = new pc.Entity(`switch-plate-${id}`);
    plate.addComponent("render", { type: "box", material: plateMat, castShadows: false });
    plate.setLocalScale(0.09, 0.14, plateDepth);
    plate.setLocalPosition(0, 1.2, plateDepth * 0.5);
    plate._aspectSwitchId = String(id);
    plate._aspectInteractable = true;
    root.addChild(plate);

    const rockerMat = new pc.StandardMaterial();
    rockerMat.diffuse = new pc.Color(0.2, 0.22, 0.25);
    rockerMat.emissive = new pc.Color(0.2, 1.0, 0.5);
    rockerMat.emissiveIntensity = on ? 1.4 : 0.05;
    rockerMat.update();
    const rocker = new pc.Entity(`switch-rocker-${id}`);
    rocker.addComponent("render", { type: "box", material: rockerMat, castShadows: false });
    rocker.setLocalScale(0.05, 0.07, 0.012);
    rocker.setLocalPosition(0, 1.2, plateDepth * 0.5 + 0.012);
    root.addChild(rocker);

    this.switches.set(String(id), { entity: root, plate, group, rockerMat });
  }

  /** Raycast switch plates at a screen point. Returns { id, t } for nearest. */
  raycastSwitch(x, y) {
    const { origin, dir } = screenRay(this.camera, x, y);
    let bestId = null;
    let bestT = Infinity;
    for (const [id, sw] of this.switches) {
      const aabb = worldAABB(sw.plate);
      if (!aabb) continue;
      const t = rayAABB(origin, dir, aabb);
      if (t !== null && t < bestT) {
        bestT = t;
        bestId = id;
      }
    }
    return bestId !== null ? { id: bestId, t: bestT } : null;
  }

  /** Flip the lights tied to a switch's group, if the player is close enough. */
  toggleSwitch(id) {
    const sw = this.switches.get(String(id));
    if (!sw) return false;

    const eye = this.camera.getPosition();
    const pos = sw.plate.getPosition();
    if (Math.hypot(eye.x - pos.x, eye.z - pos.z) > INTERACT_RANGE) return false;

    const next = !(this.groupOn.get(sw.group) ?? true);
    this._applyGroup(sw.group, next);
    this.app.fire("light:toggled", { group: sw.group, on: next });
    return true;
  }

  /** Apply on/off to every light + switch indicator sharing `group`. */
  _applyGroup(group, on) {
    this.groupOn.set(group, on);
    // Daylight gate: fixtures stay dark while the sun is up, fade in through
    // dusk, and reach full warmth at night (darkness: 0 = day .. 1 = night).
    const dusk = Math.pow(this._darkness, 1.2);
    // Area-scaled room light budget shared across the group's fixtures.
    const lightLevel = on ? 1.25 * dusk * this._groupFactor(group) : 0;
    const emissive = on ? 2.6 * dusk : 0;
    for (const l of this.lights.values()) {
      if (l.group !== group) continue;
      if (l.light?.light) l.light.light.intensity = l.intensity * lightLevel;
      if (l.bulbMat) {
        l.bulbMat.emissiveIntensity = emissive;
        l.bulbMat.update();
      }
    }
    for (const sw of this.switches.values()) {
      if (sw.group !== group || !sw.rockerMat) continue;
      sw.rockerMat.emissiveIntensity = on ? 1.4 : 0.05;
      sw.rockerMat.update();
    }
  }

  destroy() {
    this.lights.clear();
    this.switches.clear();
  }
}

export default LightSystem;
